import React, {useEffect, useState} from 'react'
import {Card, Col,  Row} from 'react-bootstrap';
import CountUp from 'react-countup';
import {useTranslation} from "react-i18next";
import DonutChart from "./Components/DonutChart";
import {COLORS} from "../../Common/constants/colors";
import {Link} from "react-router-dom";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import {capitalizeFirstLetter} from "../../utils/textUtils";
import {mockStores} from "../../data/stores";


const Overview = (props:any) => {
    const {t} = useTranslation();
    const {
        selectedCity,
        setSelectedCity,
        selectedProvince,
        setSelectedProvince,
        selectedStore,
        setSelectedStore,
        fromDate,
        toDate,
        setFromDate,
        setToDate,
        alertSeries,
        scenarioSeries,
        ruleSeries,
        backendStores = [],
        isDemoUser = false,
    } = props;


    const [cityOptions, setCityOptions] = useState<OptionType[] | null>(null);
    const [provinceOptions, setProvinceOptions] = useState<OptionType[] | null>(null);
    const [storeOptions, setStoreOptions] = useState<OptionType[] | null>(null);



    useEffect(() => {
        // For demo users, use mock stores with city/district
        if (isDemoUser) {
            const allOption = { value: '', label: t('Dashboard.all') };
            const cities = Array.from(new Set(mockStores.map((s) => s.city))).map((city) => ({
                value: city,
                label: capitalizeFirstLetter(city),
            }));

            const fullCityOptions = [allOption, ...cities];

            setCityOptions(fullCityOptions);
            setSelectedCity(allOption);
            return;
        }

        // For real users, extract cities from backend stores
        if (backendStores && backendStores.length > 0) {
            const allOption = { value: '', label: t('Dashboard.all') };
            const cityStrings: string[] = backendStores
                .filter((s: any) => s.city) // Only stores with city
                .map((s: any) => String(s.city).toLowerCase().trim());
            
            const cities = Array.from(new Set(cityStrings)).map((city: string) => ({
                value: city,
                label: capitalizeFirstLetter(city),
            }));

            const fullCityOptions = [allOption, ...cities];
            setCityOptions(fullCityOptions);
            setSelectedCity(allOption);
        } else {
            // No stores yet, just show "All"
            setCityOptions([{ value: '', label: t('Dashboard.all') }]);
            setSelectedCity({ value: '', label: t('Dashboard.all') });
        }
    }, [t, setSelectedCity, isDemoUser, backendStores]);


    useEffect(() => {
        // For demo users, use mock stores
        if (isDemoUser) {
            const filteredProvinces = selectedCity?.value
                ? mockStores.filter((s) => s.city === selectedCity.value)
                : mockStores;

            const provinces = Array.from(new Set(filteredProvinces.map((s) => s.province))).map(
                (province) => ({
                    value: province,
                    label: capitalizeFirstLetter(province),
                })
            );

            setProvinceOptions([{ value: '', label: t('Dashboard.all') }, ...provinces]);
            return;
        }

        // For real users, extract provinces from backend stores filtered by city
        if (backendStores && backendStores.length > 0) {
            const filteredStores = selectedCity?.value
                ? backendStores.filter((s: any) => s.city && s.city.toLowerCase().trim() === selectedCity.value)
                : backendStores;

            const provinceStrings: string[] = filteredStores
                .filter((s: any) => s.province) // Only stores with province
                .map((s: any) => String(s.province).toLowerCase().trim());
            
            const provinces = Array.from(new Set(provinceStrings)).map((province: string) => ({
                value: province,
                label: capitalizeFirstLetter(province),
            }));

            setProvinceOptions([{ value: '', label: t('Dashboard.all') }, ...provinces]);
        } else {
            setProvinceOptions([{ value: '', label: t('Dashboard.all') }]);
        }
    }, [selectedCity, t, isDemoUser, backendStores]);

    useEffect(() => {
        // For demo users, use mock stores with city/district filtering
        if (isDemoUser) {
            const filteredStores = mockStores.filter((store) => {
                const cityMatch = selectedCity?.value ? store.city === selectedCity.value : true;
                const provinceMatch = selectedProvince?.value ? store.province === selectedProvince.value : true;
                return cityMatch && provinceMatch;
            });

            const stores = Array.from(new Set(filteredStores.map((s) => s.name))).map((store) => ({
                value: store,
                label: capitalizeFirstLetter(store),
            }));

            setStoreOptions([{ value: '', label: t('Dashboard.all') }, ...stores]);
            return;
        }

        // For real users, use backend stores with city/district filtering
        const filteredStores = backendStores.filter((store: any) => {
            // Filter by active status
            if (store.active === false) return false;
            
            // Filter by city
            const cityMatch = selectedCity?.value 
                ? store.city && store.city.toLowerCase().trim() === selectedCity.value
                : true;
            
            // Filter by province
            const provinceMatch = selectedProvince?.value
                ? store.province && store.province.toLowerCase().trim() === selectedProvince.value
                : true;
            
            return cityMatch && provinceMatch;
        });

        const stores = filteredStores.map((store: any) => ({
            value: store.id, // Use UUID for backend stores
            label: store.name,
        }));

        setStoreOptions([{ value: '', label: t('Dashboard.all') }, ...stores]);
    }, [selectedCity, selectedProvince, t, isDemoUser, backendStores]);


    const handleCityChange = (selected: OptionType | null) => {
        setSelectedCity(selected);

        if (!selected || selected.value === "") {
            setSelectedProvince(null);
            setSelectedStore(null);
        } else {
            setSelectedProvince(null);
            setSelectedStore(null);
        }
    };

    const handleProvinceChange = (selected: any) => {
        setSelectedProvince(selected);
        setSelectedStore(null);
    };

    const handleStoreChange = (selected: any) => {
        setSelectedStore(selected);
    };


    const alerts = {
        legends: [t('Dashboard.open'),t('Dashboard.in_review'),t('Dashboard.closed')],
        colors: [COLORS.ERROR,COLORS.WARNING,COLORS.SUCCESS],
    }
    const scenarios = {
        legends: [t('Dashboard.active'), t('Dashboard.inactive')],
        colors: [COLORS.ACTIVE,COLORS.INACTIVE]
    }

    const rules = {
        legends: [t('Dashboard.active'), t('Dashboard.inactive')],
        colors: [COLORS.ACTIVE,COLORS.INACTIVE]
    }


    return(
        <React.Fragment>
            <Col xl={12}>
                <Card className="dash-mini">
                    {/* Mobile Responsive Header */}
                    <div className="card-header border-0">
                        {/* Title Row */}
                        <div className="d-flex align-items-center justify-content-between mb-3 mb-md-0">
                            <h4 className="card-title mb-3">{t('Dashboard.subtitle')}</h4>
                        </div>
                        
                        {/* Filters Row - Stacks on mobile, horizontal on desktop */}
                        <div className="d-flex mt-3 flex-column flex-md-row flex-wrap gap-2 gap-md-3 align-items-start align-items-md-center mt-3 mt-md-0">
                            {/* City Filter */}
                            <div className="d-flex align-items-center flex-grow-1 flex-md-grow-0 w-100 w-md-auto" style={{ minWidth: '200px' }}>
                                <span className="fw-semibold text-uppercase fs-12 me-2" style={{ whiteSpace: 'nowrap' }}>{t('Dashboard.city')}: </span>
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <Select
                                        options={cityOptions}
                                        value={selectedCity}
                                        onChange={handleCityChange}
                                        placeholder={t('Dashboard.select_city')}
                                        classNamePrefix="select"
                                        isSearchable={true}
                                        isDisabled={false}
                                        styles={{
                                            control: (base: any, state: any) => ({
                                                ...base,
                                                minWidth: '150px',
                                                width: '100%',
                                                fontSize: '14px',
                                                borderColor: state.isFocused ? '#86b7fe' : base.borderColor,
                                                boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : base.boxShadow,
                                            }),
                                            menu: (base: any) => ({
                                                ...base,
                                                zIndex: 9999
                                            }),
                                            container: (base: any) => ({
                                                ...base,
                                                width: '100%'
                                            })
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Province Filter */}
                            <div className="d-flex align-items-center flex-grow-1 flex-md-grow-0 w-100 w-md-auto" style={{ minWidth: '200px' }}>
                                <span className="fw-semibold text-uppercase fs-12 me-2" style={{ whiteSpace: 'nowrap' }}>{t('Dashboard.province')}: </span>
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <Select
                                        options={provinceOptions || []}
                                        value={selectedProvince}
                                        onChange={handleProvinceChange}
                                        isDisabled={!selectedCity || selectedCity.value === ""}
                                        placeholder={t('Dashboard.select_province')}
                                        classNamePrefix="select"
                                        isSearchable={true}
                                        styles={{
                                            control: (base: any, state: any) => ({
                                                ...base,
                                                minWidth: '150px',
                                                width: '100%',
                                                fontSize: '14px',
                                                borderColor: state.isFocused ? '#86b7fe' : base.borderColor,
                                                boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : base.boxShadow,
                                            }),
                                            menu: (base: any) => ({
                                                ...base,
                                                zIndex: 9999
                                            }),
                                            container: (base: any) => ({
                                                ...base,
                                                width: '100%'
                                            })
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Store Filter */}
                            <div className="d-flex align-items-center flex-grow-1 flex-md-grow-0 w-100 w-md-auto" style={{ minWidth: '200px' }}>
                                <span className="fw-semibold text-uppercase fs-12 me-2" style={{ whiteSpace: 'nowrap' }}>{t('Dashboard.store')}: </span>
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <Select
                                        options={storeOptions || []}
                                        value={selectedStore}
                                        onChange={handleStoreChange}
                                        isDisabled={!selectedProvince || selectedProvince.value === ""}
                                        placeholder={t('Dashboard.select_store')}
                                        classNamePrefix="select"
                                        isSearchable={true}
                                        styles={{
                                            control: (base: any, state: any) => ({
                                                ...base,
                                                minWidth: '150px',
                                                width: '100%',
                                                fontSize: '14px',
                                                borderColor: state.isFocused ? '#86b7fe' : base.borderColor,
                                                boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : base.boxShadow,
                                            }),
                                            menu: (base: any) => ({
                                                ...base,
                                                zIndex: 9999
                                            }),
                                            container: (base: any) => ({
                                                ...base,
                                                width: '100%'
                                            })
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Date Filters */}
                            <div className="d-flex flex-column flex-md-row gap-2 flex-grow-1 flex-md-grow-0 w-100 w-md-auto">
                                {/* From Date */}
                                <div className="d-flex align-items-center flex-grow-1 w-100 w-md-auto" style={{ minWidth: '200px' }}>
                                    <span className="fw-semibold text-uppercase fs-12 me-2" style={{ whiteSpace: 'nowrap' }}>{t('Dashboard.from')}: </span>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <Flatpickr
                                            options={{
                                                enableTime: true,
                                                dateFormat: "Y-m-d H:i",
                                            }}
                                            value={fromDate}
                                            onChange={([date]:any) => {
                                                setFromDate(date)
                                            }}
                                            className="form-control"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                                
                                {/* To Date */}
                                <div className="d-flex align-items-center flex-grow-1 w-100 w-md-auto" style={{ minWidth: '200px' }}>
                                    <span className="fw-semibold text-uppercase fs-12 me-2" style={{ whiteSpace: 'nowrap' }}>{t('Dashboard.to')}: </span>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <Flatpickr
                                            options={{
                                                enableTime: true,
                                                dateFormat: "Y-m-d H:i",
                                            }}
                                            value={toDate}
                                            onChange={([date]:any) => {
                                                setToDate(date)
                                            }}
                                            className="form-control"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <br/>
                    <br/>
                    <Card.Body className="pt-5">
                        <Row>
                            <Col lg={4} className="mini-widget pb-2 pb-lg-0">
                                <div className="d-flex align-items-end">
                                    <div className="flex-grow-1">
                                        <h2 className="mb-0 fs-24"><CountUp end={alertSeries[0]}></CountUp></h2>
                                        <Link to={"/alerts"}><h5 style={{textDecoration:"underline"}} className="text-muted fs-16 mt-2 mb-0">{t('Dashboard.alert_count')}</h5></Link>
                                    </div>
                                    <DonutChart colors={alerts.colors} series={alertSeries} labels={alerts.legends}   className="apex-charts"  />

                                </div>
                            </Col>
                            <Col lg={4} className="mini-widget pb-3 pb-lg-0">
                                <div className="d-flex align-items-end">
                                    <div className="flex-grow-1">
                                        <h2 className="mb-0 fs-24"><CountUp end={scenarioSeries[0]}></CountUp></h2>
                                        <Link to={"/scenario-list"}><h5 style={{textDecoration:"underline"}} className="text-muted fs-16 mt-2 mb-0">{t('Dashboard.scenario_count')}</h5></Link>
                                    </div>
                                    <DonutChart colors={scenarios.colors} series={scenarioSeries} labels={scenarios.legends}   className="apex-charts"  />

                                </div>
                            </Col>
                            <Col lg={4} className="mini-widget pb-3 pb-lg-0">
                                <div className="d-flex align-items-end">
                                    <div className="flex-grow-1">
                                        <h2 className="mb-0 fs-24"><CountUp end={ruleSeries[0]}></CountUp></h2>
                                        <Link to={"/rule-list"}><h5 style={{textDecoration:"underline"}} className="text-muted fs-16 mt-2 mb-0">{t('Dashboard.rule_count')}</h5></Link>
                                    </div>
                                    <DonutChart colors={rules.colors} series={ruleSeries} labels={rules.legends}   className="apex-charts"  />
                                </div>
                            </Col>
                        </Row>

                    </Card.Body>


                </Card>
            </Col>
        </React.Fragment>
    )


}
export default Overview;